import type { Investigation } from '@litlingo/client';
import { clearURLParams } from 'actions';
import EmptyTableState from 'components/EmptyTableState';
import FilterListHeaderRedesign from 'components/Filters/FilterListHeaderRedesign';
import CreateInvestigationModal from 'components/Investigations/CreateInvestigationModal';
import InvestigationListRow from 'components/Investigations/InvestigationListRow';
import InvestigationsListCount from 'components/Investigations/InvestigationsListCount';
import ListFooterHooksBased from 'components/ListFooter/ListFooterHooksBased';
import LoadingIndicator from 'components/LoadingIndicator';
import Navbar from 'components/Navbar';
import SearchInput from 'components/SearchInput';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import useInvestigations from 'hooks/investigations/useInvestigations';
import React, { useState } from 'react';
import LoadingOverlay from 'react-loading-overlay-ts';
import { useDispatch } from 'react-redux';
import logEvent from 'utils/analytics';

const InvestigationsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { data, isLoading, isFetching, refetch } = useInvestigations({ redirectOnError: true });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const toggleCreateModal = (): void => {
    setIsCreateModalOpen(!isCreateModalOpen);
  };

  const handleResetFiltersClick = (): void => {
    logEvent('investigations-list-reset-filters');
    dispatch(clearURLParams());
  };

  return (
    <div className="min-h-screen min-w-screen bg-white">
      <Navbar />
      <div className="pt-6">
        <header>
          <div className="max-w-7xl mx-auto px-6">
            <div className="mt-2 md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0 flex items-center justify-between">
                <div className="flex flex-row items-center">
                  <h2 className="title">Investigations</h2>
                  {isFetching && <LoadingIndicator className="ml-2" size="5" />}
                </div>
                <button
                  type="button"
                  className="flex flex-row justify-center button button--primary h-8 w-30 ml-2 shadow-sm rounded"
                  data-testid="create-investigation-button"
                  onClick={toggleCreateModal}
                >
                  <span>Add New</span>
                </button>
              </div>
            </div>

            <div className="mt-4 flex-shrink-0 flex justify-between items-center">
              <div className="flex flex-row gap-2">
                <SearchInput
                  resource={resourceQueryParamName.investigations}
                  field="broad_search"
                  className="h-8"
                />
                <button
                  type="button"
                  className="bg-white border-0 focus:outline-none inline-flex items-end leading-4 text text-body text-litlingo-gray-6 hover:text-litlingo-gray-4 underline"
                  data-testid="reset-filters-button"
                  onClick={handleResetFiltersClick}
                >
                  Clear Filters
                </button>
              </div>
              <InvestigationsListCount />
            </div>
          </div>
        </header>
        <main>
          <div className="h-full mt-2 mb-8 max-w-7xl mx-auto px-6">
            <LoadingOverlay
              active={isLoading}
              spinner={<LoadingIndicator size="10" />}
              fadeSpeed={0}
              styles={{
                content: (base) => ({
                  ...base,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }),
                wrapper: () => ({}),
                spinner: () => ({}),
                overlay: (base) => ({
                  ...base,
                  zIndex: 10,
                }),
              }}
            >
              <div className="table-wrapper__new h-full">
                <table className="table-wrapper__table h-full">
                  <thead>
                    <tr>
                      <FilterListHeaderRedesign
                        resource={resourceQueryParamName.investigations}
                        text="Name"
                        field="name"
                        className="table-wrapper__th--w-30 pl-4"
                      />
                      <th className="table-wrapper__new-th">Description</th>
                      <th className="table-wrapper__new-th">Discover ID</th>
                      <FilterListHeaderRedesign
                        resource={resourceQueryParamName.investigations}
                        text="Last Modified"
                        field="updated_at"
                      />
                      <th className="table-wrapper__new-th">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="table-wrapper__tbody h-full">
                    {!isFetching && data && data.records.length === 0 && (
                      <EmptyTableState
                        data-testid="empty-table-state"
                        message="There are no investigations to display"
                        colCount="5"
                      />
                    )}
                    {data?.records?.map((row: Investigation) => (
                      <InvestigationListRow key={row.uuid} investigation={row} />
                    ))}
                  </tbody>
                </table>
                <ListFooterHooksBased
                  resourceStateName={resourceQueryParamName.investigations}
                  resourceName="investigation"
                  fetchHook={useInvestigations}
                />
              </div>
            </LoadingOverlay>
          </div>
        </main>
      </div>
      {isCreateModalOpen && (
        <CreateInvestigationModal
          isModalOpen={isCreateModalOpen}
          toggleModalOpen={toggleCreateModal}
          onCreated={(): void => {
            refetch().catch(() => undefined);
          }}
        />
      )}
    </div>
  );
};

export default InvestigationsPage;
